import React from "react";
import i18n from "i18next";
import { reportError, isChunkLoadError } from "../utils/errorTracking";

// A class component cannot use the useTranslation hook; the shared i18next instance is
// already initialised by the time anything can throw. The defaults are the Mongolian text.
const tr = (key, fallback) => {
  try {
    return i18n.t(key, fallback);
  } catch (error) {
    return fallback;
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    reportError(error, {
      kind: "render",
      extra: { componentStack: (info?.componentStack || "").trim().slice(0, 300) || null },
    });
    // A page's chunk is gone because a new version was deployed under this open page:
    // load the new version without waiting for a tap. Shares index.html's guard (once in
    // 30 s, never offline, not without sessionStorage), so it cannot loop — the
    // "Дахин ачаалах" screen stays as the fallback.
    if (isChunkLoadError(error) && navigator.onLine !== false) {
      try {
        const last = Number(sessionStorage.getItem("diil_asset_reload_at")) || 0;
        if (Date.now() - last >= 30000) {
          sessionStorage.setItem("diil_asset_reload_at", String(Date.now()));
          window.location.reload();
        }
      } catch (e) {
        // no storage: keep the manual button
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // A lazy page whose chunk failed to download (usually: a new version was deployed
      // while this tab was open). "Try again" cannot help — React keeps the failed import —
      // so the way forward is loading the current version of the site.
      const chunkFailed = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#EF4444]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-[#020618] mb-2">
              {tr("common.errorTitle", "Алдаа гарлаа")}
            </h2>
            <p className="text-[#64748B] text-sm mb-6">
              {chunkFailed
                ? tr("common.errorUpdatedText", "Сайт шинэчлэгдсэн байна. Хуудсаа дахин ачаална уу.")
                : tr("common.errorText", "Уучлаарай, ямар нэг зүйл буруу боллоо. Дахин оролдоно уу.")}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={chunkFailed ? () => window.location.reload() : this.handleReset}
                className="px-6 py-3 bg-[#020618] text-white rounded-full text-sm font-medium hover:bg-[#1e293b] transition-colors"
              >
                {chunkFailed
                  ? tr("common.errorReload", "Дахин ачаалах")
                  : tr("common.errorRetry", "Дахин оролдох")}
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-white text-[#020618] rounded-full text-sm font-medium border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
              >
                {tr("common.errorHome", "Нүүр хуудас")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
